import React, { useEffect, useMemo, useState } from "react";
import {
  useUpdateAgentSite,
  useSetupAgentSiteSSL,
  useVerifyAgentSiteSSL,
  useReissueSSL,
  useDeleteAgentSiteSSL,
  useAgentCurrentSite,
} from "../../../hooks/useAiAgents";
import { useAgentSiteSocketUpdates } from "../../../hooks/useAgentSiteSocketUpdates";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";
import Modal from "../../ui/UIElements/Modal";
import { Button, Icon, Input } from "../../ui";
import { getFinitlessTemporalChatUrl } from "../../../lib/linkInBio";

const mapApiErrorMessages = (error, fallback) => {
  if (Array.isArray(error?.data?.result)) {
    return error.data.result;
  }

  if (typeof error?.data?.result === "string") {
    return [error.data.result];
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return [error.message];
  }

  return [fallback];
};

const normalizeSslStatus = (status) => {
  if (!status) return null;

  const normalizedStatus = status
    .toLowerCase()
    .trim()
    .replace(/[-\s]+/g, "_");

  if (
    normalizedStatus === "pre_issued" ||
    normalizedStatus === "pendingvalidation"
  ) {
    return "pending_validation";
  }

  if (normalizedStatus === "active") {
    return "issued";
  }

  return normalizedStatus;
};

const normalizeSslProcessStatus = (status) => {
  if (!status) return null;

  return status
    .toLowerCase()
    .trim()
    .replace(/[-\s]+/g, "_");
};

const isValidCustomDomain = (domainValue) => {
  if (!domainValue) return false;

  const normalizedDomain = domainValue.trim().toLowerCase();

  if (!normalizedDomain) return false;
  if (normalizedDomain.includes("@")) return false;
  if (normalizedDomain.startsWith("http://")) return false;
  if (normalizedDomain.startsWith("https://")) return false;
  if (normalizedDomain.includes("/")) return false;
  if (/\s/.test(normalizedDomain)) return false;

  const domainRegex =
    /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

  return domainRegex.test(normalizedDomain);
};

const getDisplayDnsRecordValue = (record) => {
  const recordType = record?.type?.toString()?.trim()?.toUpperCase?.();
  const rawValue = record?.value;

  if (typeof rawValue !== "string") {
    return rawValue;
  }

  const normalizedValue = rawValue.trim().toLowerCase().replace(/\.$/, "");

  const mappedCnameTargets = new Set([
    "cname.vercel-dns.com",
    "72eb0642bbb9ba57.vercel-dns-017.com",
  ]);
  const isVercelCnameTarget = mappedCnameTargets.has(normalizedValue);

  if (recordType === "CNAME" && isVercelCnameTarget) {
    return "cname.finitless.com";
  }

  return rawValue;
};

const AgentSitesSection = ({ agent, isAgentEnabled = true }) => {
  const { t } = useTranslation("aiAgents");
  const [isEditingDomain, setIsEditingDomain] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [actionErrors, setActionErrors] = useState([]);
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pendingDangerAction, setPendingDangerAction] = useState(null);

  const toast = useToast();

  const { data: currentSite } = useAgentCurrentSite(agent?.id);

  const site = currentSite || agent?.site || null;
  const liveAgent = useMemo(
    () => ({
      ...agent,
      site,
    }),
    [agent, site],
  );

  useAgentSiteSocketUpdates(liveAgent);

  const updateSiteMutation = useUpdateAgentSite();
  const setupSSLMutation = useSetupAgentSiteSSL();
  const verifySSLMutation = useVerifyAgentSiteSSL();
  const reissueSSLMutation = useReissueSSL();
  const deleteSSLMutation = useDeleteAgentSiteSSL();

  useEffect(() => {
    setDomainInput(site?.domain || "");
  }, [site?.domain]);

  const defaultSiteUrl = getFinitlessTemporalChatUrl(site?.temporal_domain);

  const customDomainUrl = site?.domain ? `https://${site.domain}` : null;

  const normalizedProcessStatus = normalizeSslProcessStatus(
    site?.ssl_process_status,
  );
  const isProcessing = normalizedProcessStatus === "pending";
  const normalizedStatus = normalizeSslStatus(site?.ssl_status);
  const dnsRecords = site?.deployment_provider_settings?.dns_records || [];
  const isVercelProvider =
    !site?.deployment_provider ||
    site?.deployment_provider?.toLowerCase?.() === "vercel";

  const isAnyMutationPending =
    updateSiteMutation.isPending ||
    setupSSLMutation.isPending ||
    verifySSLMutation.isPending ||
    reissueSSLMutation.isPending ||
    deleteSSLMutation.isPending;

  const getSSLStatusInfo = () => {
    if (!site?.ssl_status) {
      return {
        icon: "AlertCircle",
        color: "text-muted-foreground",
        bgColor: "bg-muted/20",
        borderColor: "border-border/30",
        label: t("sites.ssl.labels.noSsl"),
        tooltip: t("sites.ssl.tooltips.noSsl"),
      };
    }

    switch (normalizedStatus) {
      case "issued":
        return {
          icon: "CheckCircle2",
          color: "text-success",
          bgColor: "bg-[var(--success-bg)]",
          borderColor: "border-success/30",
          label: t("sites.ssl.labels.issued"),
          tooltip: t("sites.ssl.tooltips.issued"),
        };
      case "pending_validation":
        return {
          icon: "Clock",
          color: "text-warning",
          bgColor: "bg-[var(--warning-bg)]",
          borderColor: "border-warning/30",
          label: t("sites.ssl.labels.pendingValidation"),
          tooltip: isProcessing
            ? t("sites.ssl.tooltips.pendingValidationProcessing")
            : t("sites.ssl.tooltips.pendingValidation"),
        };
      case "error":
        return {
          icon: "XCircle",
          color: "text-destructive",
          bgColor: "bg-[var(--error-bg)]",
          borderColor: "border-destructive/30",
          label: t("sites.ssl.labels.error"),
          tooltip: site?.ssl_error_type
            ? t("sites.ssl.tooltips.errorWithType", {
                error: site.ssl_error_type,
              })
            : t("sites.ssl.tooltips.error"),
        };
      case "void":
        return {
          icon: "AlertCircle",
          color: "text-muted-foreground",
          bgColor: "bg-muted/20",
          borderColor: "border-border/30",
          label: t("sites.ssl.labels.void"),
          tooltip: t("sites.ssl.tooltips.void"),
        };
      default:
        return {
          icon: "AlertCircle",
          color: "text-muted-foreground",
          bgColor: "bg-muted/20",
          borderColor: "border-border/30",
          label: site.ssl_status,
          tooltip: t("sites.ssl.tooltips.unknown", {
            status: site.ssl_status,
          }),
        };
    }
  };

  const steps = [
    {
      id: "domain",
      label: t("sites.steps.domain"),
      description: t("sites.steps.domainDescription"),
      complete: Boolean(site?.domain),
    },
    {
      id: "dns",
      label: t("sites.steps.dns"),
      description: t("sites.steps.dnsDescription"),
      complete: dnsRecords.length > 0,
    },
    {
      id: "setup",
      label: t("sites.steps.setup"),
      description: t("sites.steps.setupDescription"),
      complete: normalizedStatus && normalizedStatus !== "void",
    },
    {
      id: "verify",
      label: t("sites.steps.verify"),
      description: t("sites.steps.verifyDescription"),
      complete: normalizedStatus === "issued",
    },
  ];

  const clearErrors = () => {
    setActionErrors([]);
  };

  const handleCancelEdit = () => {
    setDomainInput(site?.domain || "");
    setIsEditingDomain(false);
    clearErrors();
  };

  const handleSaveDomain = async () => {
    const trimmedDomain = domainInput.trim();

    if (!trimmedDomain) {
      toast.warning(t("sites.validation.domainRequiredMessage"), {
        title: t("sites.validation.domainRequiredTitle"),
      });
      return;
    }

    if (!isValidCustomDomain(trimmedDomain)) {
      toast.warning(t("sites.validation.domainInvalidMessage"), {
        title: t("sites.validation.domainInvalidTitle"),
      });
      return;
    }

    clearErrors();

    try {
      await updateSiteMutation.mutateAsync({
        agentId: agent.id,
        domain: trimmedDomain.toLowerCase(),
      });

      handleApiSuccess(t("sites.toasts.updateSuccess"), toast, {
        title: t("common.success"),
      });

      setIsEditingDomain(false);
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.updateErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.updateErrorTitle"),
        defaultMessage: t("sites.toasts.updateErrorMessage"),
      });
    }
  };

  const handleSetupSSL = async () => {
    clearErrors();

    try {
      await setupSSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.setupSuccess"), toast, {
        title: t("common.success"),
      });
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.setupErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.setupErrorTitle"),
        defaultMessage: t("sites.toasts.setupErrorMessage"),
      });
    }
  };

  const handleVerifySSL = async () => {
    clearErrors();

    try {
      await verifySSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.verifySuccess"), toast, {
        title: t("common.success"),
      });
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.verifyErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.verifyErrorTitle"),
        defaultMessage: t("sites.toasts.verifyErrorMessage"),
      });
    }
  };

  const handleReissueSSL = async () => {
    clearErrors();

    try {
      await reissueSSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.reissueSuccess"), toast, {
        title: t("common.success"),
      });
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.reissueErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.reissueErrorTitle"),
        defaultMessage: t("sites.toasts.reissueErrorMessage"),
      });
    }
  };

  const handleDeleteDomain = async () => {
    clearErrors();

    try {
      await deleteSSLMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("sites.toasts.deleteSuccess"), toast, {
        title: t("common.success"),
      });
      setShowDeleteConfirmation(false);
    } catch (error) {
      setActionErrors(
        mapApiErrorMessages(error, t("sites.toasts.deleteErrorMessage")),
      );
      handleApiError(error, toast, {
        title: t("sites.toasts.deleteErrorTitle"),
        defaultMessage: t("sites.toasts.deleteErrorMessage"),
      });
    }
  };

  const closeDangerActionModal = () => {
    setPendingDangerAction(null);
  };

  const handleDangerActionConfirm = async () => {
    if (pendingDangerAction === "reissue") {
      await handleReissueSSL();
    }

    if (pendingDangerAction === "delete") {
      await handleDeleteDomain();
    }

    closeDangerActionModal();
  };

  const sslInfo = getSSLStatusInfo();

  const canSetup =
    Boolean(site?.domain) &&
    !isProcessing &&
    normalizedStatus !== "pending_validation" &&
    normalizedStatus !== "issued";

  const canVerify =
    Boolean(site?.domain) &&
    !isProcessing &&
    (normalizedStatus === "pending_validation" || normalizedStatus === "error");

  const shouldShowSetupGuidance =
    Boolean(site?.domain) && normalizedStatus === "void" && !isProcessing;
  const shouldShowVerifyGuidance =
    Boolean(site?.domain) &&
    normalizedStatus === "pending_validation" &&
    !isProcessing;
  const shouldWarnIssuedDangerActions = normalizedStatus === "issued";

  const showSetupOnly = canSetup && !canVerify;
  const showVerifyOnly = canVerify && !canSetup;
  const showBothActions = canSetup && canVerify;

  const setupActionLabel =
    showSetupOnly && normalizedStatus === "void"
      ? t("sites.actions.setupSslAfterDns")
      : t("sites.actions.setupSsl");
  const dangerActionTitle =
    pendingDangerAction === "delete"
      ? t("sites.dangerActions.delete.title")
      : t("sites.dangerActions.reissue.title");
  const dangerActionMessage =
    pendingDangerAction === "delete"
      ? t("sites.dangerActions.delete.message")
      : t("sites.dangerActions.reissue.message");
  const dangerActionConfirmLabel =
    pendingDangerAction === "delete"
      ? t("sites.dangerActions.delete.confirm")
      : t("sites.dangerActions.reissue.confirm");
  const dangerActionIntent =
    pendingDangerAction === "delete" ? "danger" : "warning";

  return (
    <div className="ordering-ai-agent-panel ordering-ai-agent-sites border-border bg-card rounded-lg border p-6">
      <h3 className="text-foreground mb-4 text-lg font-semibold">
        {t("sites.title")}
      </h3>

      <div className="space-y-4">
        {defaultSiteUrl && (
          <div className="border-border bg-background/50 rounded-lg border p-4">
            <div className="text-muted-foreground mb-1 text-sm">
              {t("sites.defaultSite")}
            </div>
            {isAgentEnabled ? (
              <a
                href={defaultSiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group text-primary hover:text-primary flex items-center gap-2"
              >
                <span className="font-medium">{defaultSiteUrl}</span>
                <Icon
                  name="ExternalLink"
                  className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
                />
              </a>
            ) : (
              <div
                title={t("sites.tooltips.agentDisabledLink")}
                className="text-muted-foreground flex cursor-not-allowed items-center gap-2"
              >
                <span className="font-medium">{defaultSiteUrl}</span>
                <Icon name="ExternalLink" className="h-4 w-4 opacity-50" />
              </div>
            )}
          </div>
        )}

        <div className="border-border bg-background/50 rounded-lg border p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-muted-foreground text-sm">
              {t("sites.customDomain")}
            </div>
            {!isEditingDomain && (
              <Button
                type="button"
                emphasis="link"
                intent="brand"
                size="sm"
                onClick={() => setIsEditingDomain(true)}
                className="h-auto px-0"
                disabled={isAnyMutationPending}
              >
                {customDomainUrl
                  ? t("sites.actions.change")
                  : t("sites.actions.addDomain")}
              </Button>
            )}
          </div>

          {isEditingDomain ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-xs">
                {t("sites.domainHint")}
              </p>
              <Input
                type="text"
                value={domainInput}
                onChange={(event) => setDomainInput(event.target.value)}
                placeholder={t("sites.placeholder")}
                aria-label={t("sites.customDomain")}
                className="w-full"
                disabled={isAnyMutationPending}
              />

              <div className="flex gap-2">
                <Button
                  type="button"
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  onClick={handleSaveDomain}
                  disabled={isAnyMutationPending}
                  className="flex-1"
                >
                  {updateSiteMutation.isPending
                    ? t("actions.saving")
                    : t("actions.save")}
                </Button>
                <Button
                  type="button"
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                  onClick={handleCancelEdit}
                  disabled={isAnyMutationPending}
                  className="flex-1"
                >
                  {t("actions.cancel")}
                </Button>
              </div>
            </div>
          ) : customDomainUrl ? (
            <div className="flex items-center justify-between gap-3">
              {isAgentEnabled ? (
                <a
                  href={customDomainUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group text-primary hover:text-primary flex items-center gap-2"
                >
                  <span className="font-medium">{site.domain}</span>
                  <Icon
                    name="ExternalLink"
                    className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </a>
              ) : (
                <div
                  title={t("sites.tooltips.agentDisabledLink")}
                  className="text-muted-foreground flex cursor-not-allowed items-center gap-2"
                >
                  <span className="font-medium">{site.domain}</span>
                  <Icon name="ExternalLink" className="h-4 w-4 opacity-50" />
                </div>
              )}

              <div
                className={`flex items-center gap-2 rounded-full border px-2 py-1 ${sslInfo.bgColor} ${sslInfo.borderColor} ${sslInfo.color}`}
                title={sslInfo.tooltip}
              >
                <Icon name={sslInfo.icon} className="h-3 w-3" />
                <span className="text-xs font-medium">{sslInfo.label}</span>
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">
              {t("sites.empty.noCustomDomain")}
            </div>
          )}
        </div>

        {!isVercelProvider && (
          <div className="border-warning/30 text-warning rounded-lg border bg-[var(--warning-bg)] p-3 text-sm">
            {t("sites.providerNotSupported")}
          </div>
        )}

        {isVercelProvider && (
          <>
            <div className="border-border bg-background/50 rounded-lg border p-4">
              <p className="text-foreground mb-3 text-sm font-medium">
                {t("sites.steps.title")}
              </p>
              <ul className="space-y-2">
                {steps.map((step, index) => (
                  <li key={step.id} className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold ${
                        step.complete
                          ? "text-success bg-[var(--success-bg)]"
                          : "bg-card text-muted-foreground"
                      }`}
                    >
                      {step.complete ? (
                        <Icon name="ShieldCheck" className="h-3 w-3" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div>
                      <p className="text-foreground text-sm">{step.label}</p>
                      <p className="text-muted-foreground text-xs">
                        {step.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {isProcessing && (
              <div className="border-primary/30 bg-accent/10 text-primary flex items-center gap-2 rounded-lg border p-3 text-sm">
                <Icon
                  name="Loader2"
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                />
                <span>{t("sites.processing")}</span>
              </div>
            )}

            {actionErrors.length > 0 && (
              <div className="border-destructive/30 rounded-lg border bg-[var(--error-bg)] p-3">
                <div className="text-destructive mb-2 flex items-center gap-2 text-sm font-medium">
                  <Icon name="AlertCircle" className="h-4 w-4" />
                  {t("sites.errors.title")}
                </div>
                <ul className="text-destructive list-disc space-y-1 ps-5 text-sm">
                  {actionErrors.map((errorMessage, index) => (
                    <li key={`${errorMessage}-${index}`}>{errorMessage}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border-border bg-background/50 rounded-lg border p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-foreground text-sm font-medium">
                  {t("sites.dns.title")}
                </h4>
                <span className="text-muted-foreground text-xs">
                  {t("sites.dns.source")}
                </span>
              </div>

              {dnsRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-start text-sm">
                    <thead>
                      <tr className="border-border text-muted-foreground border-b text-xs uppercase">
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.type")}
                        </th>
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.name")}
                        </th>
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.value")}
                        </th>
                        <th className="px-2 py-2">
                          {t("sites.dns.columns.status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dnsRecords.map((record, index) => (
                        <tr
                          key={`${record.type}-${record.name}-${index}`}
                          className="border-border border-b last:border-0"
                        >
                          <td className="text-foreground px-2 py-2">
                            {record.type}
                          </td>
                          <td className="text-foreground px-2 py-2 font-mono text-xs">
                            {record.name}
                          </td>
                          <td className="text-foreground px-2 py-2 font-mono text-xs">
                            {getDisplayDnsRecordValue(record)}
                          </td>
                          <td className="px-2 py-2">
                            {record.verified ? (
                              <span className="text-success inline-flex items-center gap-1">
                                <Icon
                                  name="CheckCircle2"
                                  className="h-3.5 w-3.5"
                                />
                                {t("sites.dns.verified")}
                              </span>
                            ) : (
                              <span className="text-warning inline-flex items-center gap-1">
                                <Icon name="Clock" className="h-3.5 w-3.5" />
                                {t("sites.dns.pending")}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t("sites.dns.empty")}
                </p>
              )}
            </div>

            {shouldShowSetupGuidance && (
              <div className="border-warning/30 rounded-lg border bg-[var(--warning-bg)] p-4">
                <p className="text-warning text-sm font-semibold">
                  {t("sites.guidance.setupRequiredTitle")}
                </p>
                <p className="text-warning mt-1 text-sm">
                  {t("sites.guidance.setupRequiredDescription")}
                </p>
                <ol className="text-warning mt-2 list-decimal space-y-1 ps-5 text-xs">
                  <li>{t("sites.guidance.setupRequiredSteps.addDns")}</li>
                  <li>{t("sites.guidance.setupRequiredSteps.clickSetup")}</li>
                </ol>
              </div>
            )}

            {shouldShowVerifyGuidance && (
              <div className="border-primary/30 bg-accent/10 rounded-lg border p-4">
                <p className="text-primary text-sm font-semibold">
                  {t("sites.guidance.verifyRequiredTitle")}
                </p>
                <p className="text-primary-foreground mt-1 text-sm">
                  {t("sites.guidance.verifyRequiredDescription")}
                </p>
                <ol className="text-primary-foreground mt-2 list-decimal space-y-1 ps-5 text-xs">
                  <li>{t("sites.guidance.verifyRequiredSteps.confirmDns")}</li>
                  <li>{t("sites.guidance.verifyRequiredSteps.clickVerify")}</li>
                </ol>
              </div>
            )}

            {(showSetupOnly || showVerifyOnly || showBothActions) && (
              <div
                className={
                  showBothActions
                    ? "grid grid-cols-1 gap-2 md:grid-cols-2"
                    : "grid grid-cols-1 gap-2"
                }
              >
                {(showSetupOnly || showBothActions) && (
                  <Button
                    type="button"
                    emphasis="solid"
                    intent="brand"
                    size="touch"
                    onClick={handleSetupSSL}
                    disabled={!canSetup || isAnyMutationPending}
                    className="w-full"
                  >
                    {setupSSLMutation.isPending
                      ? t("sites.actions.settingUp")
                      : setupActionLabel}
                  </Button>
                )}

                {(showVerifyOnly || showBothActions) && (
                  <Button
                    type="button"
                    emphasis="solid"
                    intent="brand"
                    size="touch"
                    onClick={handleVerifySSL}
                    disabled={!canVerify || isAnyMutationPending}
                    className="w-full"
                  >
                    {verifySSLMutation.isPending
                      ? t("sites.actions.verifying")
                      : t("sites.actions.verifyDns")}
                  </Button>
                )}
              </div>
            )}

            <div className="border-border bg-background/50 rounded-lg border p-4">
              <Button
                onClick={() => setShowAdvancedActions((current) => !current)}
                type="button"
                emphasis="ghost"
                intent="neutral"
                size="touch"
                aria-expanded={showAdvancedActions}
                className="w-full justify-between text-start"
              >
                <span>{t("sites.advanced.title")}</span>
                <Icon
                  name="RefreshCw"
                  className={`h-4 w-4 ${showAdvancedActions ? "text-primary" : "text-muted-foreground"}`}
                />
              </Button>

              {showAdvancedActions && (
                <div className="border-border mt-3 space-y-3 border-t pt-3">
                  <Button
                    type="button"
                    emphasis="outline"
                    intent="warning"
                    size="touch"
                    onClick={() => {
                      if (shouldWarnIssuedDangerActions) {
                        setPendingDangerAction("reissue");
                        return;
                      }

                      handleReissueSSL();
                    }}
                    disabled={
                      isProcessing || isAnyMutationPending || !site?.domain
                    }
                    className="w-full"
                  >
                    {reissueSSLMutation.isPending
                      ? t("sites.actions.reissuing")
                      : t("sites.actions.reissueSsl")}
                  </Button>

                  {!showDeleteConfirmation ? (
                    <Button
                      type="button"
                      emphasis="outline"
                      intent="danger"
                      size="touch"
                      onClick={() => {
                        if (shouldWarnIssuedDangerActions) {
                          setPendingDangerAction("delete");
                          return;
                        }

                        setShowDeleteConfirmation(true);
                      }}
                      disabled={
                        isProcessing || isAnyMutationPending || !site?.domain
                      }
                      className="w-full"
                    >
                      <Icon name="Trash2" className="h-4 w-4" />
                      {t("sites.actions.deleteDomain")}
                    </Button>
                  ) : (
                    <div className="border-destructive/30 space-y-2 rounded-lg border bg-[var(--error-bg)] p-3">
                      <p className="text-destructive text-sm">
                        {t("sites.deleteConfirm.message")}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          emphasis="solid"
                          intent="danger"
                          size="touch"
                          onClick={handleDeleteDomain}
                          disabled={isAnyMutationPending}
                          className="flex-1"
                        >
                          {deleteSSLMutation.isPending
                            ? t("sites.actions.deleting")
                            : t("sites.actions.confirmDelete")}
                        </Button>
                        <Button
                          type="button"
                          emphasis="outline"
                          intent="neutral"
                          size="touch"
                          onClick={() => setShowDeleteConfirmation(false)}
                          disabled={isAnyMutationPending}
                          className="flex-1"
                        >
                          {t("actions.cancel")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={Boolean(pendingDangerAction)}
        onClose={closeDangerActionModal}
        title={dangerActionTitle}
        subtitle={t("sites.dangerActions.subtitle")}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          <div className="border-warning/30 rounded-lg border bg-[var(--warning-bg)] p-4">
            <div className="flex items-start gap-3">
              <Icon
                name="AlertTriangle"
                className="text-warning mt-0.5 h-5 w-5"
              />
              <div>
                <p className="text-warning text-sm font-medium">
                  {t("sites.dangerActions.warningTitle")}
                </p>
                <p className="text-warning mt-1 text-sm">
                  {dangerActionMessage}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              emphasis="outline"
              intent="neutral"
              size="touch"
              onClick={closeDangerActionModal}
              disabled={isAnyMutationPending}
              className="flex-1"
            >
              {t("sites.dangerActions.cancel")}
            </Button>
            <Button
              type="button"
              emphasis="solid"
              intent={dangerActionIntent}
              size="touch"
              onClick={handleDangerActionConfirm}
              disabled={isAnyMutationPending}
              className="flex-1"
            >
              {isAnyMutationPending
                ? t("actions.saving")
                : dangerActionConfirmLabel}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentSitesSection;
